import React from 'react';
import financial_visualisations from '../assets/financial_visualisations.png';
import AdBanner from '../components/AdBanner';

/* ── journey steps ────────────────────────────────────────────── */
const journeySteps = [
  {
    level: 1,
    colour: 'blue',
    icon: '📄',
    title: 'The Financial Statements',
    subtitle: 'Where every journey begins',
    body: `Every company tells its story in three documents. The Income Statement shows what a company earned and spent over a quarter — revenue at the top, net income at the bottom, with gross profit, operating expenses and EBITDA in between. The Balance Sheet is a snapshot of what the company owns (assets), what it owes (liabilities) and what belongs to shareholders (equity). The Cash Flow Statement reveals the cash that actually moved — operating cash flow, capital expenditure, free cash flow, dividends and buybacks.`,
    appFeature: 'Luminoso surfaces 36 line-items from these three statements so you can read a company\'s financials at a glance, compare quarters, and spot where the numbers are heading.',
    ring: 'ring-blue-400',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    accent: 'bg-blue-600',
  },
  {
    level: 2,
    colour: 'emerald',
    icon: '📐',
    title: 'Ratios & Advanced Metrics',
    subtitle: 'Turning raw numbers into insight',
    body: `Raw figures only take you so far. A company with $10 billion in revenue sounds impressive — until you discover its margins are shrinking quarter over quarter. Ratios normalise the data so you can compare a mega-cap with a mid-cap on equal footing. Profitability ratios (gross margin, operating margin, ROE, ROA, ROIC) tell you how efficiently a company converts revenue into returns. Efficiency ratios (asset turnover, DSO, DIO, DPO, cash conversion cycle) reveal how well the business manages its working capital. Leverage ratios (debt-to-equity, interest coverage, net-debt-to-EBITDA) show how much financial risk the company carries.`,
    appFeature: 'Luminoso computes 36 advanced metrics across three cards — Profitability | Efficiency | Growth, Leverage | Solvency | Liquidity, and Profitability | Valuation — giving you a complete ratio dashboard for any publicly traded company.',
    ring: 'ring-emerald-400',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    accent: 'bg-emerald-600',
  },
  {
    level: 3,
    colour: 'amber',
    icon: '🔍',
    title: 'Earnings Quality',
    subtitle: 'Can you trust the numbers?',
    body: `Not all profits are created equal. A company can report rising net income while its cash flow tells a different story. Earnings quality analysis asks: are these earnings backed by real cash, or are they propped up by accounting choices? The Accruals Ratio measures the gap between reported profit and cash generated. CFO-to-Net-Income checks whether operating cash flow supports the bottom line. The Sloan Ratio isolates the accrual component of earnings — high accruals often precede poor future performance. The Days Sales Receivable Index flags aggressive revenue recognition. Together these signals form an Earnings Quality Score: a single composite that tells you how much to trust what the income statement is saying.`,
    appFeature: 'Luminoso\'s Earnings Quality card computes five signals and a composite score for every quarter, so you can verify whether a company\'s reported profits are sustainable before you rely on them.',
    ring: 'ring-amber-400',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    accent: 'bg-amber-600',
  },
  {
    level: 4,
    colour: 'indigo',
    icon: '💎',
    title: 'Valuation',
    subtitle: 'Is the price right?',
    body: `You now understand the business — its profitability, efficiency, leverage and earnings quality. The final question is: what is it worth? Valuation multiples compare a company's market price to its underlying fundamentals. Price-to-Earnings (P/E) relates the share price to earnings per share. Price-to-Book (P/B) compares market value to the net assets on the balance sheet. EV/EBITDA strips out capital structure to give an enterprise-level view. Dividend Yield and Buyback Yield show how much cash is flowing back to shareholders. Revenue per Share and Book Value per Share ground the abstract price in operational reality. These metrics don't tell you whether to buy — they tell you what the market is currently paying, so you can decide whether it's too much, too little, or about right.`,
    appFeature: 'Luminoso\'s Valuation card presents eight multiples using today\'s share price against each quarter\'s fundamentals — the classic "trailing multiples" approach used by professional analysts.',
    ring: 'ring-indigo-400',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
    accent: 'bg-indigo-600',
  },
  {
    level: 5,
    colour: 'purple',
    icon: '📈',
    title: 'Visualisations & Trends',
    subtitle: 'Seeing the story across time',
    body: `A single quarter is a data point. Six quarters is a trend. Visualisations bring everything together — they let you see margin compression, watch debt rising against coverage, track whether returns on capital are expanding or fading, and confirm that free cash flow is consistently exceeding net income. The Returns on Capital chart (ROE, ROA, ROIC) is the centrepiece for any value investor. The Profitability Cascade shows revenue anchored against three margin lines dropping from gross to operating to net — revealing exactly where value is being created or lost. FCF vs Net Income is the visual earnings-quality test. Debt & Coverage Profile, Equity & Book Value, Efficiency (CCC), and Cash Flow Allocation round out the picture.`,
    appFeature: 'Luminoso offers ten purpose-built chart views, each selectable from a dropdown, with configurable quarter ranges and one-click PNG/CSV export — so the data travels with you into presentations, spreadsheets and reports.',
    ring: 'ring-purple-400',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    accent: 'bg-purple-600',
  },
];

/* ── component ────────────────────────────────────────────────── */
const Metrics_Text = () => {
  return (
    <div className="w-full my-4 space-y-6">

      {/* ── Hero / About ── */}
      <div className="card p-6 md:p-8 w-full">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">About Luminoso</h2>
        <p className="text-gray-600 leading-relaxed text-base mb-4">
          Luminoso helps investors understand how businesses actually work. Built by
          an engineer, the platform bridges the gap between the income statement and
          the factory floor — surfacing the operational drivers behind financial
          performance. Whether you are evaluating your first stock or managing a
          portfolio, Luminoso gives you the same data professional analysts use,
          presented clearly and honestly.
        </p>
        <p className="text-gray-600 leading-relaxed text-base">
          We believe anyone can be an analyst. You don't need an MBA or a Bloomberg
          terminal. You need the right metrics, in the right order, with the right
          context. That's what this platform — and the learning journey below — is
          designed to provide.
        </p>
      </div>

      {/* ── Learning Journey ── */}
      <div className="card p-6 md:p-8 w-full">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">
          Your Learning Journey
        </h2>
        <p className="text-gray-500 text-sm mb-6">
          Five levels, from reading a financial statement to valuing a business.
          Every feature in Luminoso maps to a stage on this path.
        </p>

        <div className="relative">
          {/* vertical connector line */}
          <div className="hidden md:block absolute left-6 top-8 bottom-8 w-0.5 bg-gray-200" />

          <div className="space-y-8">
            {journeySteps.map((step) => (
              <div key={step.level} className="relative flex gap-5">
                {/* level badge */}
                <div className="flex-shrink-0 z-10">
                  <div
                    className={`w-12 h-12 rounded-full ${step.accent} text-white flex items-center justify-center font-bold text-lg shadow-md`}
                  >
                    {step.level}
                  </div>
                </div>

                {/* content card */}
                <div
                  className={`flex-1 rounded-xl border ${step.border} ${step.bg} p-5`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">{step.icon}</span>
                    <h3 className={`text-lg font-bold ${step.text}`}>
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
                    {step.subtitle}
                  </p>

                  <p className="text-gray-700 leading-relaxed text-sm mb-3">
                    {step.body}
                  </p>

                  <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2">
                    <span className="text-sm mt-0.5">💡</span>
                    <p className="text-gray-600 text-sm italic leading-relaxed">
                      <span className="font-semibold not-italic">In Luminoso:</span>{' '}
                      {step.appFeature}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Getting Started ── */}
      <div className="card p-6 md:p-8 w-full">
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Getting Started</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { icon: '📊', title: 'Pick a Company', text: 'Use the dropdown to select any publicly traded stock. Luminoso fetches the latest quarterly data from Yahoo Finance.' },
            { icon: '🔄', title: 'Refresh & Explore', text: 'Hit Refresh Metrics to pull fresh data. Switch quarters with the Quarter dropdown to compare periods side by side.' },
            { icon: '🎓', title: 'Learn as You Go', text: 'Hover the ⓘ icon on any metric for a plain-English definition. Visit the Definitions page for the full glossary.' },
            { icon: '📈', title: 'Visualise & Export', text: 'Open the Visualisations page to chart trends across quarters. Download PNG charts or CSV data with one click.' },
          ].map((card, i) => (
            <div
              key={i}
              className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center"
            >
              <span className="text-2xl">{card.icon}</span>
              <h4 className="font-semibold text-gray-800 mt-2 mb-1 text-sm">
                {card.title}
              </h4>
              <p className="text-gray-500 text-xs leading-relaxed">{card.text}</p>
            </div>
          ))}
        </div>

        <div className="flex justify-center">
          <img
            src={financial_visualisations}
            alt="Financial Visualisation"
            className="max-w-full h-auto rounded-lg shadow-card"
          />
        </div>
      </div>

      {/* ── Ad banner ── */}
      <div className="w-full">
        <AdBanner />
      </div>
    </div>
  );
};

export default Metrics_Text;

